import { useLayoutEffect, useState, type RefObject } from 'react';

export interface FlipPositionInput {
  anchorRect: DOMRect | null;
  menuRef: RefObject<HTMLElement>;
  defaultMenuHeight?: number;
  defaultMenuWidth?: number;
}

// useFlipPosition computes a `{ top, left }` for a fixed-positioned popup
// anchored beneath `anchorRect`. When the popup would overflow the bottom of
// the viewport, it flips above the anchor; when it would overflow the right
// edge, it clamps horizontally. Pass a ref to the popup's root element so
// the hook can read its measured size on first paint.
export function useFlipPosition({
  anchorRect,
  menuRef,
  defaultMenuHeight = 260,
  defaultMenuWidth = 320,
}: FlipPositionInput): { top: number; left: number } {
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!anchorRect) return;
    const node = menuRef.current;
    const mh = node?.offsetHeight ?? defaultMenuHeight;
    const mw = node?.offsetWidth ?? defaultMenuWidth;
    const vw = typeof window === 'undefined' ? 1024 : window.innerWidth;
    const vh = typeof window === 'undefined' ? 768 : window.innerHeight;

    const wantTopBelow = anchorRect.bottom + 4;
    const wantTopAbove = anchorRect.top - mh - 4;
    const overflowBottom = wantTopBelow + mh > vh - 8;
    const top = overflowBottom && wantTopAbove >= 8 ? wantTopAbove : wantTopBelow;

    const wantLeft = anchorRect.left;
    const overflowRight = wantLeft + mw > vw - 8;
    const left = overflowRight ? Math.max(8, vw - mw - 8) : wantLeft;

    setPos({ top, left });
  }, [anchorRect, menuRef, defaultMenuHeight, defaultMenuWidth]);

  return pos;
}
